import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { tenantApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { ImageCropModal } from "@/components/ImageCropModal";
import { Icon } from "@/components/Icon";

import type { CompanyItem as Company } from "@/pages/Companies/types";

interface EditCompanyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (company: Company) => void;
    company: Company | null;
}

export const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    company,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [name, setName] = useState<string>("");
    const [database, setDatabase] = useState<string>("");
    const [vigepra1, setVigepra1] = useState<string>("");
    const [logoUrl, setLogoUrl] = useState<string>("");
    const [brandColor, setBrandColor] = useState<string>("#31628d");
    const [loading, setLoading] = useState<boolean>(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("");
    const [isLogoCropOpen, setIsLogoCropOpen] = useState<boolean>(false);
    const [nameError, setNameError] = useState<boolean>(false);
    const nameRef = useRef<HTMLDivElement>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && company) {
            setName(company.data?.name || company.name || "");
            // If vigepra1 already set, use it; otherwise pre-fill with current database value
            const currentDb = company.data?.database || company.database || "";
            const currentVigepra1 =
                company.data?.vigepra1 || company.vigepra1 || "";
            setVigepra1(currentVigepra1 || currentDb);
            // Leave database empty so user fills the new one
            setDatabase(currentVigepra1 ? currentDb : "");
            setLogoUrl(company.data?.logo_url || company.logo_url || "");
            setBrandColor(
                company.data?.brand_color || company.brand_color || "#31628d"
            );
            setLogoFile(null);
            setIsLogoCropOpen(false);
            setNameError(false);
        }
    }, [isOpen, company]);

    useEffect(() => {
        if (!logoFile) {
            setLogoPreview("");
            return;
        }

        const preview = URL.createObjectURL(logoFile);
        setLogoPreview(preview);

        return () => URL.revokeObjectURL(preview);
    }, [logoFile]);

    const handleSubmit = async (): Promise<void> => {
        const missingName = !name.trim();

        if (missingName) {
            setNameError(true);
            showError(t("companies.validation_required"));
            nameRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        setLoading(true);
        try {
            const res = await tenantApi.update(company!.id, {
                name,
                database: database.trim() || null,
                vigepra1: vigepra1.trim() || null,
                logo_url: logoUrl.trim() || null,
                brand_color: brandColor,
            });

            let updatedCompany = res.data?.company || {
                ...company,
                name,
                database,
                vigepra1,
            };

            if (logoFile) {
                const logoResponse = await tenantApi.uploadLogo(
                    company!.id,
                    logoFile
                );
                updatedCompany = logoResponse.data?.company || updatedCompany;
            }

            showSuccess(t("companies.updated"));
            onSuccess(updatedCompany);
            onClose();
        } catch (error: unknown) {
            const msg =
                error instanceof Error
                    ? error.message
                    : t("companies.error_update");
            showError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("companies.edit_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div>
                    <div className="mb-3 text-center text-sm font-semibold text-slate-700 dark:text-zinc-200">
                        {t("companies.logo")}
                    </div>
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(event) => {
                            const file = event.target.files?.[0];

                            if (file) {
                                setLogoFile(file);
                                setIsLogoCropOpen(true);
                            }

                            event.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        className="group relative mx-auto flex h-24 w-24 items-center justify-center overflow-visible rounded-full border-2 border-primary bg-primary text-2xl font-black text-white shadow-md"
                        onClick={() => logoInputRef.current?.click()}
                        title={t("account_avatar.change")}
                    >
                        {logoPreview ? (
                            <img
                                src={logoPreview}
                                alt=""
                                className="h-full w-full rounded-full object-cover"
                            />
                        ) : logoUrl ? (
                            <img
                                src={logoUrl}
                                alt=""
                                className="h-full w-full rounded-full object-cover"
                            />
                        ) : (
                            (name || "E").trim().charAt(0).toUpperCase()
                        )}
                        <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm transition-transform group-hover:scale-110">
                            <Icon value="pencil" size={13} />
                        </div>
                    </button>
                </div>
                <div ref={nameRef}>
                    <Input
                        label={t("companies.name")}
                        value={name}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>

                <Input
                    label={t("companies.database_current")}
                    value={database}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setDatabase(e.target.value)
                    }
                    placeholder="app_emp_nombreempresa"
                    //helperText={t('companies.database_current_hint')}
                />

                <Input
                    label={t("companies.database_vigepra1")}
                    value={vigepra1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setVigepra1(e.target.value)
                    }
                    placeholder="app_emp_nombreempresa_v1"
                    helperText={t("companies.database_vigepra1_hint")}
                />

                <div className="flex items-end gap-3">
                    <Input
                        label={t("companies.brand_color")}
                        type="color"
                        value={brandColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setBrandColor(e.target.value)
                        }
                        className="h-10 w-16 cursor-pointer p-1"
                    />
                    {/*
                    <div className="pb-2 text-xs text-slate-500 dark:text-zinc-400">
                        {t("companies.brand_color_hint")}
                    </div>
                    */}
                </div>
            </div>
            <ImageCropModal
                isOpen={isLogoCropOpen}
                file={logoFile}
                title={t("companies.logo")}
                onClose={() => setIsLogoCropOpen(false)}
                onConfirm={async (file) => {
                    setLogoFile(file);
                    setIsLogoCropOpen(false);
                }}
            />
        </Modal>
    );
};
